import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import LoadingScreen from "@/components/LoadingScreen";

type StripeDiagnosticsResponse = {
  stripe?: {
    account_id: string;
    country: string | null;
    default_currency: string | null;
    livemode: boolean;
  };
  app?: {
    expected_webhook_url: string;
    has_matching_webhook_endpoint: boolean;
  };
  webhook_endpoints?: {
    count: number;
    matching: any;
    all: any[];
  };
  recent_payment_intents?: {
    count: number;
    items: any[];
  };
  error?: string;
};

const upsertMeta = (name: string, content: string) => {
  let el = document.querySelector(`meta[name=\"${name}\"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertCanonical = (href: string) => {
  let el = document.querySelector(`link[rel=\"canonical\"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

export default function StripeDiagnostics() {
  const { user } = useAuth();
  const { isAdmin, loading: roleLoading } = useUserRole();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<StripeDiagnosticsResponse | null>(null);

  useEffect(() => {
    document.title = "Stripe Diagnostics | AnySkill";
    upsertMeta(
      "description",
      "Stripe diagnostics for AnySkill: verify Stripe mode, webhook endpoints, and recent payment intents."
    );
    upsertCanonical(`${window.location.origin}/admin/stripe-diagnostics`);
  }, []);

  const summary = useMemo(() => {
    if (!data?.stripe || !data?.app) return null;
    return {
      mode: data.stripe.livemode ? "LIVE" : "TEST",
      accountId: data.stripe.account_id,
      expectedWebhookUrl: data.app.expected_webhook_url,
      hasMatchingWebhook: data.app.has_matching_webhook_endpoint,
    };
  }, [data]);

  const run = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("stripe-diagnostics");
      if (error) throw error;
      setData(data as StripeDiagnosticsResponse);
      toast.success("Stripe diagnostics loaded");
    } catch (e: any) {
      const msg = e?.message || "Failed to load diagnostics";
      toast.error(msg);
      setData({ error: msg });
    } finally {
      setLoading(false);
    }
  };

  if (roleLoading) return <LoadingScreen />;
  if (!user || !isAdmin) return <Navigate to="/" replace />;

  return (
    <main className="min-h-screen bg-background py-12">
      <div className="container mx-auto px-4 space-y-6">
        <header className="space-y-2">
          <h1 className="text-3xl font-semibold text-foreground">Stripe Diagnostics</h1>
          <p className="text-muted-foreground">
            Read-only view of your real Stripe account: mode, webhook endpoints, and recent PaymentIntents.
          </p>
        </header>

        <section className="flex flex-wrap items-center gap-3">
          <Button onClick={run} disabled={loading}>
            {loading ? "Running…" : "Run diagnostics"}
          </Button>
        </section>

        {summary && (
          <section className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle>Stripe Mode</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Badge variant={summary.mode === "TEST" ? "secondary" : "destructive"}>{summary.mode}</Badge>
                  <span className="text-sm text-muted-foreground">(based on Stripe account livemode)</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Account</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm text-foreground font-mono break-all">{summary.accountId}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Webhook Endpoint</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="text-xs text-muted-foreground">Expected URL</div>
                <div className="text-sm text-foreground font-mono break-all">{summary.expectedWebhookUrl}</div>
                <div className="pt-2">
                  {summary.hasMatchingWebhook ? (
                    <Badge>Found in Stripe</Badge>
                  ) : (
                    <Badge variant="destructive">NOT found in Stripe</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          </section>
        )}

        {summary && !summary.hasMatchingWebhook && (
          <section>
            <Alert variant="destructive">
              <AlertTitle>Stripe is not configured to call Supabase</AlertTitle>
              <AlertDescription>
                The Stripe account behind your current API key does not have a webhook endpoint matching the expected Supabase URL.
                Create the endpoint in the same Stripe account + same mode and select the required events.
              </AlertDescription>
            </Alert>
          </section>
        )}

        {data?.error && (
          <section>
            <Alert variant="destructive">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{data.error}</AlertDescription>
            </Alert>
          </section>
        )}

        {data && (
          <section>
            <Card>
              <CardHeader>
                <CardTitle>Raw response</CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="max-h-[520px] overflow-auto rounded-md border bg-muted p-4 text-xs text-foreground">
                  {JSON.stringify(data, null, 2)}
                </pre>
              </CardContent>
            </Card>
          </section>
        )}
      </div>
    </main>
  );
}
